import React, {useCallback} from 'react';
import {
    Title,
    Text,
    Flex,
    GridContainer,
    GridItem,
    Input
} from '@priority-software/priority-style-react';
import {ControlWrapper, SubmitButton, FormMessage} from '../../components';
import {useForm} from '../../hooks';
import {getFormModel} from './form.model';
import {Content} from '../TabsContent/Content';
import {Footer} from '../TabsContent/Footer';
import {AsyncSelect} from '../../components/AsyncSelect';
import {priority} from '@priority-software/priority-proto-api/lib';
import {formName} from './constants';
import texts from '../../texts';
import styles from './styles.module.scss';
import {resetFormChangesFlag} from '../../hooks/useForm';
import {sendMonitor} from '../../../../utils/monitoring';
import {encodeToUi8Array} from '@priority-software/priority-utils/lib/file-processing';
import {TwoColumnOption} from '../../components/Select/TwoColumnOption';
import {
    AttachmentType,
    FileUploaderController,
    ImageUploaderControl
} from '../../../../components/FileUploader';

type Props = {
    openCompanyScreen: () => void;
    goNext: () => void;
    companyData: any;
    isSubmitting: boolean;
    isReadOnly: boolean;
    saveCompanyData: (data: any) => void;
    fieldUpdate: (val: any, name: string) => void;
    onHyperLinkClick: (linkData: {eName: string}) => void;
};

const selectMenuWidth = 360;

export const CompanyForm = (props: Props) => {
    const {
        goNext,
        isSubmitting,
        openCompanyScreen,
        companyData,
        saveCompanyData,
        fieldUpdate,
        onHyperLinkClick,
        isReadOnly
    } = props;

    const {formState, isFormValid, handleChange, isDirty, updateFormState} = useForm(
        getFormModel(companyData)
    );

    const isFormDisabled = !isFormValid || !isDirty;

    const saveData = useCallback(async () => {
        const preparedData = Object.keys(formState).reduce(
            (prev: any, key) => ({
                ...prev,
                [key]: formState[key].value
            }),
            {}
        );

        await saveCompanyData(preparedData);
    }, [formState, saveCompanyData]);

    const onSaveDetails = useCallback(async () => {
        sendMonitor('OnboardingTabsCompanySave');
        await saveData();
        resetFormChangesFlag();
        goNext();
    }, [goNext, saveData]);

    const onGoNext = () => {
        sendMonitor('OnboardingTabsCompanyNext');
        goNext();
    };

    const onLogoChange = useCallback(
        async (file: AttachmentType) => handleChange(file, 'EXTFILENAME'),
        [handleChange]
    );

    const renderTwoColumnItem = useCallback(args => {
        return <TwoColumnOption {...args} />;
    }, []);

    const onChange = useCallback(
        async e => {
            const {value, name} = e.target;
            await handleChange(value, name);
        },
        [handleChange]
    );

    const onChangeSelect = useCallback(
        async (option, actionData) => {
            handleChange(option.value, actionData.name);
            await fieldUpdate(option.value, actionData.name);

            if (actionData.name === 'COUNTRYNAME') {
                // reset state on country name change
                await handleChange('', 'STATENAME');
            }
        },
        [handleChange, fieldUpdate]
    );

    const onCountryChange = useCallback(
        async (option, actionData) => {
            const countryField = actionData.name;
            const stateField = 'STATENAME';
            const countryValue = option.value;
            const stateHasValue = formState[stateField]?.value;

            const countryData = {
                ...formState[actionData.name],
                value: countryValue
            };
            const stateData = {
                ...formState[stateField],
                value: ''
            };

            const updatedFormState = {
                ...formState,
                [countryField]: countryData,
                [stateField]: stateData
            };

            updateFormState(updatedFormState);

            await fieldUpdate(countryValue, countryField);

            if (stateHasValue) {
                // reset state value on the server
                await fieldUpdate('', stateField);
            }
        },
        [formState, fieldUpdate, updateFormState]
    );

    const setFile = async (files: File[]) => {
        const file = files[0];
        try {
            if (file.type.match(/image.*/)) {
                const imgMaxWidth = 300;
                const imgMaxHeight = 150;
                const reader = new FileReader();

                reader.onload = readerEvent => {
                    const image = new Image();
                    image.src = readerEvent.target.result as any;

                    image.onload = async imageEvent => {
                        // Resize the image
                        const canvas = document.createElement('canvas');

                        let width = image.width;
                        let height = image.height;

                        if (width > imgMaxWidth) {
                            height = height * (imgMaxWidth / width);
                            width = imgMaxWidth;
                        }

                        if (height > imgMaxHeight) {
                            width = width * (imgMaxHeight / height);
                            height = imgMaxHeight;
                        }

                        canvas.width = width;
                        canvas.height = height;
                        canvas.getContext('2d').drawImage(image, 0, 0, width, height);

                        const dataUrl = canvas.toDataURL(file.type);
                        const resizedImage = await encodeToUi8Array(dataUrl);

                        const fileData: AttachmentType = {
                            Data: resizedImage,
                            MimeType: file.type
                        };

                        onLogoChange(fileData);
                    };
                };
                reader.readAsDataURL(file);
            }
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <FileUploaderController
            file={formState.EXTFILENAME.value}
            onChange={onLogoChange}
            componentClasses={styles.wrap}
            setFile={setFile}
            title={texts.dropzone_backdrop_text}
        >
            <Content>
                <div className={styles.component}>
                    <div className="tabHeader">
                        <Title skin="h2" textColor="primary-500" componentClasses="tabTitle">
                            {texts.company_tab_title}
                        </Title>

                        <Text textColor="primary-700" skin="paragraph-1" fontWeight="normal">
                            {isReadOnly
                                ? texts.company_readonly_subtitle
                                : texts.company_tab_subtitle}
                        </Text>
                    </div>

                    <section className={styles.fieldset}>
                        <GridContainer columns={4} padding={1} componentClasses={styles.form}>
                            <GridItem componentClasses="formCol" offset={1} span={2}>
                                <ControlWrapper
                                    readOnly
                                    onHyperLinkClick={onHyperLinkClick}
                                    label={texts.company_name_label}
                                    readOnlyValue={companyData?.COMPDES}
                                    hasTooltip
                                    fieldName="COMPDES"
                                    formName={formName}
                                />
                            </GridItem>

                            <GridItem componentClasses="formCol" offset={3} span={2}>
                                <ControlWrapper
                                    readOnly
                                    onHyperLinkClick={onHyperLinkClick}
                                    label={texts.tax_id_label}
                                    readOnlyValue={companyData?.VATNUM}
                                    fieldName="VATNUM"
                                    formName={formName}
                                    hasTooltip
                                />
                            </GridItem>

                            <GridItem componentClasses="formCol" offset={1} span={4}>
                                <ControlWrapper
                                    onHyperLinkClick={onHyperLinkClick}
                                    label={texts.company_number}
                                    readOnlyValue={companyData?.WTAXNUM}
                                    fieldName="WTAXNUM"
                                    readOnly
                                />
                            </GridItem>

                            <GridItem componentClasses="formCol" offset={1} span={4}>
                                <ImageUploaderControl
                                    label={
                                        <Text skin="paragraph-2" textColor="primary-700">
                                            {texts.logo_label}
                                        </Text>
                                    }
                                    placeholder={texts.logo_placeholder}
                                    monitor={{send: true, text: 'OnboardingTabsCompanyLogo'}}
                                />
                            </GridItem>
                        </GridContainer>
                    </section>

                    <section className={styles.fieldset}>
                        <GridContainer columns={4} padding={1} componentClasses={styles.form}>
                            <GridItem componentClasses="formCol" offset={1} span={2}>
                                <ControlWrapper
                                    onHyperLinkClick={onHyperLinkClick}
                                    label={texts.street_label}
                                    readOnlyValue={companyData?.ADDRESS}
                                    readOnly={isReadOnly}
                                    error={formState.ADDRESS.error}
                                    fieldName="ADDRESS"
                                    required
                                >
                                    <Input
                                        autocomplete="off"
                                        size="large"
                                        onChange={onChange}
                                        value={formState.ADDRESS.value}
                                        placeholder={texts.street_placeholder}
                                    />
                                </ControlWrapper>
                            </GridItem>

                            <GridItem componentClasses="formCol" offset={3} span={2}>
                                <ControlWrapper
                                    onHyperLinkClick={onHyperLinkClick}
                                    readOnly={isReadOnly}
                                    label={texts.city_label}
                                    readOnlyValue={companyData?.STATEB}
                                    error={formState.STATEB.error}
                                    fieldName="STATEB"
                                    required
                                >
                                    <Input
                                        autocomplete="off"
                                        onChange={onChange}
                                        value={formState.STATEB.value}
                                        size="large"
                                        placeholder={texts.city_placeholder}
                                    />
                                </ControlWrapper>
                            </GridItem>

                            <GridItem componentClasses="formCol" offset={1} span={2}>
                                <ControlWrapper
                                    onHyperLinkClick={onHyperLinkClick}
                                    readOnly={isReadOnly}
                                    label={texts.country_label}
                                    readOnlyValue={companyData?.COUNTRYNAME}
                                    error={formState.COUNTRYNAME.error}
                                    fieldName="COUNTRYNAME"
                                    required
                                >
                                    <AsyncSelect
                                        horizontalMenuPosition="left"
                                        menuWidth={selectMenuWidth}
                                        formName={formName}
                                        value={formState.COUNTRYNAME.value}
                                        onChange={onCountryChange}
                                        renderItem={renderTwoColumnItem}
                                        placeholder={texts.country_placeholder}
                                        Preferred={priority.netitems.shared.FieldSelectType.Choose}
                                        size="large"
                                    />
                                </ControlWrapper>
                            </GridItem>

                            <GridItem componentClasses="formCol" offset={3} span={2}>
                                <ControlWrapper
                                    onHyperLinkClick={onHyperLinkClick}
                                    readOnly={isReadOnly}
                                    readOnlyValue={companyData?.STATENAME}
                                    label={texts.state_label}
                                    error={formState.STATENAME.error}
                                    fieldName="STATENAME"
                                >
                                    <AsyncSelect
                                        horizontalMenuPosition="right"
                                        menuWidth={selectMenuWidth}
                                        renderItem={renderTwoColumnItem}
                                        onChange={onChangeSelect}
                                        value={formState.STATENAME.value}
                                        placeholder={texts.state_placeholder}
                                        Preferred={priority.netitems.shared.FieldSelectType.Choose}
                                        formName={formName}
                                        size="large"
                                    />
                                </ControlWrapper>
                            </GridItem>

                            <GridItem componentClasses="formCol" offset={1} span={2}>
                                <ControlWrapper
                                    onHyperLinkClick={onHyperLinkClick}
                                    readOnly={isReadOnly}
                                    readOnlyValue={companyData?.ZIP}
                                    error={formState.ZIP.error}
                                    label={texts.zipcode_label}
                                    fieldName="ZIP"
                                >
                                    <Input
                                        autocomplete="off"
                                        size="large"
                                        onChange={onChange}
                                        value={formState.ZIP.value}
                                        placeholder={texts.zipcode_placeholder}
                                    />
                                </ControlWrapper>
                            </GridItem>
                        </GridContainer>
                    </section>

                    <section className={styles.fieldset}>
                        <GridContainer columns={4} padding={1} componentClasses={styles.form}>
                            <GridItem componentClasses="formCol" offset={1} span={2}>
                                <ControlWrapper
                                    onHyperLinkClick={onHyperLinkClick}
                                    readOnly={isReadOnly}
                                    readOnlyValue={companyData?.WEBSITE}
                                    label={texts.website_label}
                                    error={formState.WEBSITE.error}
                                    fieldName="WEBSITE"
                                >
                                    <Input
                                        autocomplete="off"
                                        onChange={onChange}
                                        size="large"
                                        value={formState.WEBSITE.value}
                                        placeholder={texts.website_placeholder}
                                    />
                                </ControlWrapper>
                            </GridItem>

                            <GridItem componentClasses="formCol" offset={3} span={2}>
                                <ControlWrapper
                                    readOnly
                                    onHyperLinkClick={onHyperLinkClick}
                                    readOnlyValue={companyData?.EMAIL}
                                    label={texts.email_label}
                                    fieldName="EMAIL"
                                />
                            </GridItem>

                            <GridItem componentClasses="formCol" offset={1} span={2}>
                                <ControlWrapper
                                    onHyperLinkClick={onHyperLinkClick}
                                    label={texts.phone_label}
                                    readOnlyValue={companyData?.PHONE}
                                    readOnly={isReadOnly}
                                    error={formState.PHONE.error}
                                    fieldName="PHONE"
                                    required
                                >
                                    <Input
                                        autocomplete="off"
                                        onChange={onChange}
                                        size="large"
                                        value={formState.PHONE.value}
                                        placeholder={texts.phone_placeholder}
                                    />
                                </ControlWrapper>
                            </GridItem>
                        </GridContainer>
                    </section>
                </div>

                <FormMessage
                    componentClasses={styles.formMessage}
                    btnHandler={openCompanyScreen}
                    btnText={texts.company_readonly_footer_btn}
                    text={texts.company_readonly_footer_text}
                />
            </Content>

            <Footer>
                <Flex justifyContent="flex-end">
                    {isReadOnly ? (
                        <SubmitButton componentClasses={styles.submit} onClick={onGoNext}>
                            {texts.next_label}
                        </SubmitButton>
                    ) : (
                        <SubmitButton
                            loading={isSubmitting}
                            componentClasses={styles.submit}
                            onClick={onSaveDetails}
                            disabled={isFormDisabled}
                        >
                            {texts.company_tab_submit}
                        </SubmitButton>
                    )}
                </Flex>
            </Footer>
        </FileUploaderController>
    );
};
